import Layout from "@/layouts/layout";
import PageHeading from "@/components/layout/PageHeading";
import Panel from "@/components/ui/Panel";
import Button from "@/components/ui/Button";
import { ButtonLink } from "@/components/ui/Button";
import {
  Field,
  Input,
  Textarea,
  Select,
  Checkbox,
} from "@/components/ui/Field";
import { useForm, router } from "@inertiajs/react";

type Requirement = {
  id?: number;
  title?: string;
  description?: string | null;
  kind?: string;
  position?: number;
  required?: boolean;
  active?: boolean;
  due_offset_days?: number | null;
  event_stage_id?: number | null;
  resource_url?: string | null;
  resource_label?: string | null;
};

type StageOption = { id: number; name: string };

const KIND_HINTS: Record<string, string> = {
  confirmation: "A checkbox the organizer ticks.",
  text: "A short written answer.",
  number: "A number, like a headcount.",
  url: "The organizer hands back a link - a signed agreement, a photo album.",
  watch: "Something to watch. Put the video link in Resource URL.",
  read: "Something to read. Put a guide path like /guides/organizer-handbook in Resource URL.",
  venue:
    "Ticks itself once the organizer fills in the venue and date on their event, so the address isn't typed twice.",
};

// Only these kinds point at something to go and consume.
const RESOURCE_KINDS = ["watch", "read"];

export default function AdminRequirementEdit({
  requirement,
  kinds,
  stages,
}: {
  requirement: Requirement;
  kinds: string[];
  stages: StageOption[];
}) {
  const isNew = !requirement.id;

  const { data, setData, post, patch, processing, errors } = useForm({
    title: requirement.title ?? "",
    description: requirement.description ?? "",
    kind: requirement.kind ?? "confirmation",
    position: requirement.position ?? 0,
    required: requirement.required ?? true,
    active: requirement.active ?? true,
    due_offset_days: requirement.due_offset_days ?? "",
    event_stage_id: requirement.event_stage_id ?? "",
    resource_url: requirement.resource_url ?? "",
    resource_label: requirement.resource_label ?? "",
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (isNew) {
      post("/admin/requirements");
    } else {
      patch(`/admin/requirements/${requirement.id}`);
    }
  }

  return (
    <Layout>
      <div className="flex flex-col gap-8">
        <PageHeading
          eyebrow="Admin"
          title={isNew ? "New requirement" : requirement.title || "Requirement"}
          actions={
            <ButtonLink href="/admin/requirements" variant="secondary">
              All requirements
            </ButtonLink>
          }
        />

        <form onSubmit={submit}>
          <Panel title="requirement.form" className="max-w-2xl">
            <div className="flex flex-col gap-5">
              <Field label="Title" error={errors.title}>
                <Input
                  value={data.title}
                  onChange={(e) => setData("title", e.target.value)}
                  placeholder="Confirm your venue"
                  required
                />
              </Field>

              <Field
                label="Description"
                hint="Shown under the title on the organizer's checklist."
                error={errors.description}
              >
                <Textarea
                  rows={2}
                  value={data.description}
                  onChange={(e) => setData("description", e.target.value)}
                />
              </Field>

              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <Field
                  label="Kind"
                  hint={KIND_HINTS[data.kind]}
                  error={errors.kind}
                >
                  <Select
                    value={data.kind}
                    onChange={(e) => setData("kind", e.target.value)}
                  >
                    {kinds.map((k) => (
                      <option key={k} value={k}>
                        {k}
                      </option>
                    ))}
                  </Select>
                </Field>

                <Field
                  label="Position"
                  hint="Lower sorts first, within the stage."
                  error={errors.position}
                >
                  <Input
                    type="number"
                    value={data.position}
                    onChange={(e) =>
                      setData("position", Number(e.target.value))
                    }
                  />
                </Field>
              </div>

              <Field
                label="Stage"
                hint="Which group of steps this belongs to. Leave unassigned and it lands in Everything else."
                error={errors.event_stage_id}
              >
                <Select
                  value={String(data.event_stage_id ?? "")}
                  onChange={(e) => setData("event_stage_id", e.target.value)}
                >
                  <option value="">Unassigned</option>
                  {stages.map((stage) => (
                    <option key={stage.id} value={stage.id}>
                      {stage.name}
                    </option>
                  ))}
                </Select>
              </Field>

              {/* Only watch/read send the organizer somewhere. */}
              {RESOURCE_KINDS.includes(data.kind) && (
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <Field
                    label="Resource URL"
                    hint="A video link, or a path on this site like /guides/organizer-handbook."
                    error={errors.resource_url}
                  >
                    <Input
                      value={data.resource_url}
                      onChange={(e) => setData("resource_url", e.target.value)}
                      placeholder="https://… or /guides/…"
                    />
                  </Field>

                  <Field
                    label="Link label"
                    hint='Defaults to "Watch it" / "Read it".'
                    error={errors.resource_label}
                  >
                    <Input
                      value={data.resource_label}
                      onChange={(e) =>
                        setData("resource_label", e.target.value)
                      }
                      placeholder="3 min video"
                    />
                  </Field>
                </div>
              )}

              <Field
                label="Due (days from event start)"
                hint="Negative is before the event. -7 means a week before. Blank for no deadline."
                error={errors.due_offset_days}
              >
                <Input
                  type="number"
                  value={data.due_offset_days}
                  onChange={(e) => setData("due_offset_days", e.target.value)}
                  placeholder="-7"
                  className="max-w-40"
                />
              </Field>

              <label className="flex items-center gap-3">
                <Checkbox
                  checked={data.required}
                  onChange={(e) => setData("required", e.target.checked)}
                />
                <span className="font-display text-subheading font-bold">
                  Required
                </span>
              </label>

              <label className="flex items-center gap-3">
                <Checkbox
                  checked={data.active}
                  onChange={(e) => setData("active", e.target.checked)}
                />
                <span className="font-display text-subheading font-bold">
                  Active
                </span>
              </label>

              <div className="flex flex-wrap gap-3">
                <Button type="submit" disabled={processing}>
                  {processing ? "Saving…" : isNew ? "Add requirement" : "Save"}
                </Button>

                {!isNew && (
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      if (confirm(`Remove "${requirement.title}"?`)) {
                        router.delete(`/admin/requirements/${requirement.id}`);
                      }
                    }}
                  >
                    Remove
                  </Button>
                )}
              </div>
            </div>
          </Panel>
        </form>
      </div>
    </Layout>
  );
}
